import {
  Button,
  ButtonProps,
  Column,
  EmptyState,
  FlexBox,
  IconButton,
  Portal,
  Text,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { ampli } from 'lib-shared-tracking';
import { useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { searchActions } from '../../search.actions';

import { FilterAddCondition } from './components/addCondition/filterAddCondition';
import { FilterCondition } from './components/condition/filterCondition';
import { SearchQuickFilters } from './components/quickFilters/searchQuickFilters';
import { FilterStrategy } from './components/strategy/filterStrategy';
import { useSearchFilters } from './searchFilters.context';
import styles from './searchFilters.module.scss';
import { filtersInitialState } from './utils/definitions';

/** Represents a component that is used to visualize the search filters. */
export const SearchFilters = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const flags = useFeatureFlag(['new-search-conversations-view']);
  const { params } = useAppSelector((state) => state.search);
  const [open, setOpen] = useState(false);

  const {
    strategy,
    filters,
    activeCount,
    advancedActiveCount,
    validation: { isEmpty, isValid, validate },
    reset,
  } = useSearchFilters();

  const draftCount = filters.length;

  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  /**
   * When the last filter condition is removed, we clear the filters from the
   * store.
   */
  useUpdateEffect(() => {
    if (params.filters.filters.length && !filters.length) {
      clearFilters();
    }
  }, [filters.length]);

  /** Handles opening and closing the sidebar and sends the tracking events. */
  const handleOpen = (newValue: boolean) => {
    setOpen(newValue);

    const trackingProps = { count: activeCount };
    if (newValue) {
      ampli.searchFiltersOpened(trackingProps);
    } else {
      ampli.searchFiltersClosed(trackingProps);
    }
  };

  /** If valid, it updates the store with the new filters. */
  const applyFilters = () => {
    const isValid = validate();

    if (isValid) {
      dispatch(
        searchActions.params.update({
          filters: { strategy, filters },
          page: 1,
        }),
      );

      ampli.searchFiltersApplied({
        count: draftCount,
        hasKeyword: !!params.query,
      });

      if (isSmallScreen) {
        handleOpen(false);
      }
    }
  };

  /** Restores the filters store to its initial value. */
  const clearFilters = () => {
    reset();

    dispatch(
      searchActions.params.update({
        filters: filtersInitialState,
        page: 1,
        sortBy: undefined,
        isStrict: false,
      }),
    );

    if (!params.query) {
      dispatch(searchActions.savedSearch.close());
    }

    ampli.searchFiltersCleared({ count: activeCount });

    if (isSmallScreen) {
      handleOpen(false);
    }
  };

  const baseButtonProps: ButtonProps = {
    variant: 'secondary',
    onClick: () => handleOpen(true),
  };

  return (
    <>
      {isSmallScreen ? (
        <IconButton
          {...baseButtonProps}
          icon="filter"
          className={classnames(styles.iconButton, {
            [styles.iconButtonActive]: activeCount > 0,
          })}
        />
      ) : (
        <>
          {flags.newSearchConversationsView ? (
            <>
              <SearchQuickFilters />

              <Button
                {...baseButtonProps}
                label={advancedActiveCount || undefined}
              >
                More filters
              </Button>
            </>
          ) : (
            <Button {...baseButtonProps} label={activeCount || undefined}>
              Filters
            </Button>
          )}
        </>
      )}

      <Portal containerId="app">
        <aside
          className={classnames(styles.sidebar, {
            [styles.sidebarOpen]: open,
          })}
          aria-hidden={!open}
          data-testid="sidebar"
        >
          <Column>
            <Column.Row slottable noMinHeight>
              <Column.Slot placement="start">
                <Text.Heading variant="heading-3">Filters</Text.Heading>
                {!isEmpty && <FilterStrategy />}
              </Column.Slot>

              <Column.Slot placement="end">
                <Button variant="tertiary" onClick={() => handleOpen(false)}>
                  Close
                </Button>
              </Column.Slot>
            </Column.Row>

            <Column.Row scrollable className={styles.conditions}>
              {isEmpty && (
                <FlexBox flexDirection="column" alignItems="center" gap="300">
                  <EmptyState
                    icon="filter"
                    title="No filters added yet"
                    description="Add your own filters to control what data you'd like to include (and exclude) from your results."
                  />

                  <FilterAddCondition variant="primary" text="Add filter" />
                </FlexBox>
              )}

              {filters.map((condition, index) => (
                <FilterCondition
                  key={index}
                  index={index}
                  condition={condition}
                />
              ))}

              {!isEmpty && (
                <FilterAddCondition
                  variant="tertiary"
                  text="Add more filters"
                />
              )}
            </Column.Row>

            {!isEmpty && (
              <Column.Row slottable noMinHeight>
                <Column.Slot placement="start">
                  <Button variant="tertiary" onClick={clearFilters}>
                    Clear all
                  </Button>
                </Column.Slot>

                <Column.Slot placement="end">
                  <Button
                    variant="primary"
                    onClick={applyFilters}
                    disabled={!isValid}
                  >
                    Apply filters
                  </Button>
                </Column.Slot>
              </Column.Row>
            )}
          </Column>
        </aside>
      </Portal>
    </>
  );
};
